import React, { useRef, useEffect, useState } from "react";
import { motion, useInView, useScroll, useSpring, useMotionValue, animate } from "framer-motion";

// Scroll reveal wrapper
export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

// Line-by-line masked reveal for headings
export const MaskLines = ({ lines, className = "", start = false, stagger = 0.12 }) => (
  <span className={className} style={{ display: "block" }}>
    {lines.map((ln, i) => (
      <span key={i} style={{ display: "block", overflow: "hidden" }}>
        <motion.span
          style={{ display: "block" }}
          initial={{ y: "110%" }}
          animate={start ? { y: "0%" } : {}}
          transition={{ duration: 0.9, delay: 0.15 + i * stagger, ease: [0.22, 1, 0.36, 1] }}
        >
          {ln}
        </motion.span>
      </span>
    ))}
  </span>
);

// Editorial marquee
export const Marquee = ({ items, className = "" }) => {
  const loop = [...items, ...items];
  return (
    <div className={`marquee ${className}`} data-testid="brand-marquee" aria-hidden="true">
      <div className="marquee__track">
        {loop.map((t, i) => (
          <span className="marquee__item" key={i}>
            {t}
            <span className="marquee__dot" style={{ marginLeft: 28 }} />
          </span>
        ))}
      </div>
    </div>
  );
};

export const WhatsAppFloat = ({ href }) => (
  <a className="wa-float" href={href} target="_blank" rel="noopener noreferrer"
     aria-label="Chat on WhatsApp" data-testid="whatsapp-float-btn">
    <svg viewBox="0 0 32 32"><path d="M16 3C9 3 3.3 8.6 3.3 15.5c0 2.7.8 5.2 2.2 7.3L3 29l6.4-2.4c2 .9 4.2 1.4 6.6 1.4 7 0 12.7-5.6 12.7-12.5S23 3 16 3zm0 22.7c-2.1 0-4.1-.6-5.8-1.6l-.4-.2-3.8 1.4 1.4-3.7-.3-.4c-1.1-1.7-1.7-3.7-1.7-5.7 0-5.9 4.9-10.7 10.9-10.7S27 9.6 27 15.5 21.9 25.7 16 25.7zm6-8.2c-.3-.2-1.9-1-2.2-1.1-.3-.1-.5-.2-.7.2-.2.3-.8 1.1-1 1.3-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.6-1.6-1-.9-1.6-2-1.8-2.3-.2-.3 0-.5.1-.7.1-.1.3-.4.5-.6.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.1.2 2.1 3.3 5.2 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.3.2-1.5-.1-.1-.3-.2-.6-.4z"/></svg>
  </a>
);

// Count-up number when in view
export const CountUp = ({ to, suffix = "", duration = 1.8, className = "", style = {} }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration, ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  return <span ref={ref} className={className} style={style}>{val.toLocaleString()}{suffix}</span>;
};

// Top scroll progress bar
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return (
    <motion.div aria-hidden="true"
      style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, transformOrigin: "0%",
        background: "linear-gradient(90deg,var(--red),var(--gold))", zIndex: 1200, scaleX }} />
  );
};

// Magnetic button (subtle pull toward cursor)
export const MagneticButton = ({ children, className = "", strength = 0.35, ...rest }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.button ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ x: sx, y: sy }} className={className} {...rest}>
      {children}
    </motion.button>
  );
};

// Program icon set (stroke inherits from CSS)
export const ProgramIcon = ({ name }) => {
  const p = { fill: "none", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  const map = {
    boxing: <><path d="M7 12a5 5 0 0 1 10 0v3a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" {...p} /><path d="M9 12V9a3 3 0 0 1 6 0v3" {...p} /><path d="M5 15l2 1M19 15l-2 1" {...p} /></>,
    kick: <><circle cx="12" cy="5" r="2.2" {...p} /><path d="M12 7.5v6l-4 8M12 13.5l4 8M8 11l-4 3M16 11l4 3" {...p} /></>,
    target: <><circle cx="12" cy="12" r="9" {...p} /><path d="M12 3v18M3 12h18" {...p} /></>,
    shield: <path d="M12 2l7 4v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6z" {...p} />,
    dumbbell: <path d="M4 12h2M18 12h2M8 12h8M6 9v6M18 9v6" {...p} />,
    pulse: <path d="M4 19l5-11 4 7 3-5 4 9" {...p} />,
  };
  return <svg viewBox="0 0 24 24">{map[name]}</svg>;
};
